import React, {useCallback, useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";

import {AppContext} from "../../HomePage";
import {Loading} from "../../utils/InlineComponents";
import {isAdmin} from "../../utils/Utils";
import ProbeLauncher from "./ProbeLauncher";
import {CampaignService} from "../../service/CampaignService";

const campaignService = new CampaignService();

// Reusable competitor-discovery candidates list (campaign.competitor_candidates):
// sites selling the campaign's products, ranked by product_count, with readiness
// + e-commerce tags. Used by the onboarding Sites step and the campaign
// "My Sellers' URLs" panel. Adding a candidate -> campaign competitor (monitored).
const CompetitorCandidates = ({campaignId, onChanged, onOpenSiteDefinition, probeWebsites, onLoaded}) => {
    const {t, toast, setError} = React.useContext(AppContext);
    const [candidates, setCandidates] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [linksRow, setLinksRow] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');

    const reload = useCallback(() => {
        if (!campaignId) return;
        setLoading(true);
        campaignService.getCompetitorCandidates(campaignId)
            .then(data => {
                setCandidates(data || []);
                if (onLoaded) onLoaded(data || []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [campaignId, onLoaded]);

    useEffect(() => {
        reload();
    }, [reload]);

    const fail = (error) => {
        setError(error);
        setError(null);
    };
    const after = () => {
        setSelected([]);
        reload();
        if (onChanged) onChanged();
    };

    const candidateUrl = (row) => 'https://' + row.host;

    const readiness = (row) => {
        if (row.is_site_searching_ready)
            return {label: t('onboarding.sites.ready'), severity: 'success'};
        if (row.is_spider_ready)
            return {label: t('onboarding.sites.needsSearch'), severity: 'warning'};
        return {label: t('onboarding.sites.needsSpider'), severity: 'danger'};
    };

    const candStatus = (row) => row.is_site_searching_ready ? 'ready' : row.is_spider_ready ? 'needsSearch' : 'needsSpider';
    const filteredCandidates = statusFilter === 'all' ? candidates : candidates.filter(r => candStatus(r) === statusFilter);

    const addCandidate = (row) => {
        campaignService.addCampaignCompetitor(campaignId, [{url: candidateUrl(row)}])
            .then(() => {
                toast.current.show({severity: 'success', summary: t('onboarding.sites.added')});
                after();
            }).catch(fail);
    };

    const addSelected = () => {
        const rows = selected.filter(r => !r.is_monitored);
        if (!rows.length) return;
        Promise.all(rows.map(r => campaignService.addCampaignCompetitor(campaignId, [{url: candidateUrl(r)}])))
            .then(() => {
                toast.current.show({severity: 'success', summary: t('onboarding.sites.added')});
                setSelected([]);
                after();
            }).catch(fail);
    };

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap'}}>
                <strong>{t('onboarding.sites.competitors')} ({candidates.length})</strong>
                <span style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                    {isAdmin() && candidates.length > 0 &&
                        <Dropdown value={statusFilter} style={{width: '15em'}} onChange={(e) => setStatusFilter(e.value)}
                                  options={[
                                      {label: t('onboarding.sites.filterAll', 'Tümü'), value: 'all'},
                                      {label: t('onboarding.sites.ready', 'arama-hazır'), value: 'ready'},
                                      {label: t('onboarding.sites.needsSearch', 'site-içi arama yok'), value: 'needsSearch'},
                                      {label: t('onboarding.sites.needsSpider', 'spider gerekli'), value: 'needsSpider'},
                                  ]}/>}
                    <Button icon="pi pi-refresh" className="p-button-text p-button-sm" onClick={reload} disabled={loading}
                            tooltip={t('actions.refresh')}/>
                    <Button label={t('onboarding.sites.addSelected') + (selected.length ? ` (${selected.length})` : '')}
                            icon="pi pi-plus" className="p-button-sm"
                            disabled={!selected.length} onClick={addSelected}/>
                </span>
            </div>

            {loading
                ? <div style={{textAlign: 'center', padding: '2em'}}>
                    <Loading/>
                    <div style={{marginTop: 10, color: '#6b7280'}}>{t('onboarding.sites.loadingCompetitors', 'Rakipler yükleniyor…')}</div>
                </div>
                : candidates.length === 0
                ? <Message severity="info"
                           text={t('onboarding.sites.competitorsEmpty')}/>
                : (
                    <DataTable value={filteredCandidates} dataKey="host" selection={selected}
                               onSelectionChange={(e) => setSelected(e.value)}
                               responsiveLayout="scroll" paginator={filteredCandidates.length > 10} rows={10}
                               emptyMessage={t('onboarding.sites.noMatch', 'Filtreyle eşleşen site yok.')}>
                        <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                        <Column header={t('onboarding.sites.site')}
                                body={(row) =>
                                    <a href={candidateUrl(row)} target="_blank" rel="noopener noreferrer"
                                       onClick={(e) => e.stopPropagation()}>{row.host}</a>}/>
                        <Column header={t('onboarding.sites.foundLinks')} style={{width: '7em'}}
                                body={(row) => (row.urls && row.urls.length) ?
                                    <Button icon="pi pi-link" className="p-button-sm p-button-outlined" label={`${row.urls.length}`}
                                            tooltip={t('onboarding.sites.foundLinksHint', 'Bu sitede bulunan örnek ürün linklerini gör.')}
                                            tooltipOptions={{position: 'bottom'}}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setLinksRow(row);
                                            }}/> : null}/>
                        <Column header={t('onboarding.sites.productCount')} style={{width: '9em'}}
                                body={(row) => <Tag value={`${row.product_count || 0}`} severity="info"/>}/>
                        {isAdmin() &&
                        <Column header={t('onboarding.sites.status')} style={{width: '22em'}}
                                body={(row) => (
                                    <span style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                                        {row.is_ecommerce &&
                                            <Tag value={t('onboarding.sites.ecommerceTag')} icon="pi pi-shopping-cart" severity="info"/>}
                                        {(() => {
                                            const r = readiness(row);
                                            return <Tag value={r.label} severity={r.severity}/>;
                                        })()}
                                        {row.is_monitored &&
                                            <Tag value={t('onboarding.sites.monitoredTag')} severity="success"/>}
                                        {probeWebsites && probeWebsites.has(row.host) &&
                                            <Tag value={t('onboarding.sites.probeExists', 'probe var')} icon="pi pi-bolt" severity="info"/>}
                                    </span>
                                )}/>}
                        <Column style={{width: '14em'}} body={(row) =>
                            <span style={{display: 'flex', gap: 4, alignItems: 'center'}} onClick={(e) => e.stopPropagation()}>
                                <Button icon="pi pi-plus" className="p-button-sm" label={t('actions.add')}
                                        disabled={row.is_monitored}
                                        tooltip={t('onboarding.sites.addToMonitoredHint', 'Bu siteyi izlenen sitelere ekle.')}
                                        tooltipOptions={{position: 'bottom'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            addCandidate(row);
                                        }}/>
                                {isAdmin() &&
                                    <ProbeLauncher siteUrl={candidateUrl(row)}
                                                   productUrl={row.urls && row.urls[0] && row.urls[0].url}
                                                   referenceProductId={row.urls && row.urls[0] && row.urls[0].product_id}
                                                   campaignId={campaignId} onStarted={onChanged}
                                                   buttonClassName="p-button-sm"
                                                   buttonTooltip={t('onboarding.sites.startProbeHint', 'Bu site/ürün için tarama (probe) denemesi başlat.')}/>}
                                {onOpenSiteDefinition && probeWebsites && probeWebsites.has(row.host) &&
                                    <Button icon="pi pi-arrow-right" className="p-button-sm p-button-outlined"
                                            tooltip={t('onboarding.sites.openSiteDefinitionHint', 'Bu host\'un spider/xpath tanımına git.')}
                                            tooltipOptions={{position: 'bottom'}}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onOpenSiteDefinition(row.host);
                                            }}/>}
                            </span>}/>
                    </DataTable>
                )}

            <Dialog header={t('onboarding.sites.foundLinks') + (linksRow ? ' — ' + linksRow.host : '')}
                    visible={!!linksRow} style={{width: '640px', maxWidth: '95vw'}} modal
                    onHide={() => setLinksRow(null)}>
                {linksRow && (linksRow.urls || []).map((item, index) => (
                    <div key={index} style={{padding: '5px 0', wordBreak: 'break-all'}}>
                        <a href={item.url} target="_blank" rel="noopener noreferrer">{item.url}</a>
                    </div>
                ))}
                {linksRow && (!linksRow.urls || linksRow.urls.length === 0) &&
                    <Message severity="info" text="-"/>}
            </Dialog>
        </div>
    );
};

export default CompetitorCandidates;
